import axios from 'axios'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { Alert } from '../../components/Alert'
import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { Input } from '../../components/FormElements/Input'
import { ErrorCode } from '../../enumns/errors'
import { api } from '../../services/api'
import { getErrorMessage } from '../../utils/getErrorMessage'

type FormValues = {
  email: string
}

type FeedbackState = string | null | undefined

const schema = yup.object().shape({
  email: yup
    .string()
    .email('FIELD_MUST_BE_AN_EMAIL')
    .required('REQUIRED_FIELD'),
})

export const ForgotPassword = () => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const [intlErrorCode, setIntlErrorCode] = useState<FeedbackState>()
  const [intlSuccessCode, setIntlSuccessCode] = useState<FeedbackState>()

  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const ondSendRecoverPasswordEmail = async (data: FormValues) => {
    try {
      await api.post('/auth/forgot-password', data)
      setIntlSuccessCode('EMAIL_SUCCESSFULLY_SENT')
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          return setIntlErrorCode(ErrorCode.EMAIL_NOT_FOUND)
        }
      }

      setIntlErrorCode(getErrorMessage(error))
    }
  }

  useEffect(() => {
    if (formState.isSubmitting) {
      setIntlErrorCode(null)
      setIntlSuccessCode(null)
    }
  }, [formState.isSubmitting])

  return (
    <AuthLayout
      title={formatMessage({ id: 'TITLE_FORGOT_PASSWORD' })}
      subtitle={formatMessage({ id: 'SUBTITLE_FORGOT_PASSWORD' })}
      withNavbar
    >
      {intlSuccessCode && (
        <div className="mb-4">
          <Alert
            variant="success"
            fullWidth
            onClose={() => setIntlSuccessCode(null)}
          >
            {formatMessage({ id: intlSuccessCode })}
          </Alert>
        </div>
      )}

      {intlErrorCode && (
        <div className="mb-4">
          <Alert
            variant="error"
            fullWidth
            onClose={() => setIntlErrorCode(null)}
          >
            {formatMessage({ id: intlErrorCode })}
          </Alert>
        </div>
      )}

      <form
        onSubmit={handleSubmit(ondSendRecoverPasswordEmail)}
        className="flex flex-col justify-between h-full grow"
      >
        <section className="md:mb-14">
          <Controller
            name="email"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { invalid, isDirty, error },
            }) => (
              <Input
                label="E-mail*"
                isValid={!invalid && isDirty}
                value={value}
                placeholder="exemple@email.com.br"
                onChange={onChange}
                errorMessage={
                  error?.message && formatMessage({ id: error.message })
                }
              />
            )}
          />
        </section>

        <footer className="flex flex-col gap-4 md:flex-row-reverse">
          <div className="flex-1">
            <Button
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              type="submit"
              borderRadius="no-rounded"
              fullWidth
            >
              {formatMessage({
                id: formState.isSubmitting ? 'SENDING' : 'CONTINUE',
              })}
            </Button>
          </div>

          <div className="flex-1">
            <Button
              disabled={formState.isSubmitting}
              type="button"
              variant="outline"
              borderRadius="no-rounded"
              fullWidth
              onClick={() => navigate('/expert')}
            >
              {formatMessage({ id: 'CANCEL' })}
            </Button>
          </div>
        </footer>
      </form>
    </AuthLayout>
  )
}
